import _ from "lodash";
import moment from "moment-timezone";
import React from "react";
import { useMappedState } from "redux-react-hook";

import { IStore } from "src/core/shared/store";

/**
 * This hook takes a delivery date and compares it with shutdowns
 * to figure out if we need to extend the lead time for that delivery date
 * based on the extendLeadTime option on a shutdown.
 * Each day in a shutdown between delivery date and cutoff date will extend the lead time by a day
 *
 * @returns number of days to extend the lead time by
 */
export const useShutdownLeadTimeExtend = () => {
  const globalShutdowns = useMappedState(({ global }: IStore) => (global.settings?.shutdowns || null));

  const getShutdownExtendDates = React.useCallback((orderDeliveryDate: Date, orderCutoff: Date, orderVariantId?: number) => {
    if (globalShutdowns?.length) {
      const datesToExtend: string[] = [];

      for (const shutdownInfo of globalShutdowns) {
        const shutdown = shutdownInfo[ "shutdown" ];

        if (orderVariantId && shutdown.options?.excludeVariants?.length && shutdown.options.excludeVariants.includes(orderVariantId)) {
          continue;
        }

        // no need to do anything if not using the extendLeadTime option
        if (!shutdown.extendLeadTime) {
          continue;
        }
        const date = moment.utc(shutdown.startDate).clone();

        // if shutdown is just for one day
        if (shutdown.startDate === shutdown.endDate) {
          // check if shutdown date is between cutoff and delivery date
          if (date.isBetween(orderCutoff, moment.utc(orderDeliveryDate), "day", "[]")) {
            datesToExtend.push(date.format("YYYY-MM-DD"));
          }
        } else {
          // loop through each day in the shutdown if multi day
          while (date.isSameOrBefore(moment.utc(shutdown.endDate), "day")) {
            if (date.isBetween(orderCutoff, moment.utc(orderDeliveryDate), "day", "[]")) {
              datesToExtend.push(date.format("YYYY-MM-DD"));
            }

            date.add(1, "days");
          }
        }
      }

      // returns the number of days to extend by
      return _.uniq(datesToExtend).length;
    }

    return 0;
  }, [ globalShutdowns ]);

  return getShutdownExtendDates;
};
